import React, { useEffect, useState, useCallback } from 'react';
import {
  Plus, DoorOpen, Search, LogIn, LogOut as LogOutIcon, Check, X as XIcon,
  UserCircle, MessageCircle, Printer, Users, RefreshCw, Filter
} from 'lucide-react';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';
import { format } from 'date-fns';

export default function GateManagement() {
  const { user } = useAuthStore();
  const isGateStaff = ['security', 'admin', 'registrar', 'superadmin'].includes(user?.role || '');
  const isStudent = user?.role === 'student';
  const [tab, setTab] = useState(isGateStaff ? 'log' : 'passes');

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><h1 className="page-title">Gate Management</h1><p className="page-subtitle">Entry/exit register, visitor log and gate passes</p></div>
      </div>
      <div className="tabs">
        {isGateStaff && <button className={`tab ${tab === 'log' ? 'active' : ''}`} onClick={() => setTab('log')}>Gate Log</button>}
        {isGateStaff && <button className={`tab ${tab === 'oncampus' ? 'active' : ''}`} onClick={() => setTab('oncampus')}>On Campus</button>}
        <button className={`tab ${tab === 'passes' ? 'active' : ''}`} onClick={() => setTab('passes')}>Gate Passes</button>
        {!isStudent && <button className={`tab ${tab === 'visitors' ? 'active' : ''}`} onClick={() => setTab('visitors')}>Visitor Requests</button>}
      </div>
      {tab === 'log' && isGateStaff && <GateLog />}
      {tab === 'oncampus' && isGateStaff && <OnCampus />}
      {tab === 'passes' && <GatePasses isGateStaff={isGateStaff} />}
      {tab === 'visitors' && !isStudent && <VisitorRequests isGateStaff={isGateStaff} />}
    </div>
  );
}

function OnCampus() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchOnCampus = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/gate/on-campus'); setRows(r.data.data.on_campus || []); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchOnCampus();
    const interval = setInterval(fetchOnCampus, 30000);
    return () => clearInterval(interval);
  }, [fetchOnCampus]);

  const personName = (l: any) => {
    if (l.person_type === 'student') return `${l.student_first_name || ''} ${l.student_last_name || ''} (${l.student_number || 'Student'})`;
    if (l.person_type === 'staff') return `${l.staff_first_name || ''} ${l.staff_last_name || ''} (Staff)`;
    return `${l.visitor_name} (Visitor)`;
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div className="badge badge-success" style={{ fontSize: 13, padding: '6px 14px' }}>{rows.length} currently on campus</div>
        <button className="btn btn-secondary btn-sm" onClick={fetchOnCampus} disabled={loading}><RefreshCw size={13} />Refresh</button>
      </div>

      {loading && rows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 60 }}><div className="loader loader-dark" style={{ margin: '0 auto' }} /></div>
      ) : rows.length === 0 ? (
        <div className="card"><div className="empty-state"><Users size={48} /><p>Nobody is currently checked in.</p></div></div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: 'var(--bg)' }}>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600 }}>Person</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600 }}>Type</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600 }}>Vehicle</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600 }}>Purpose / Host</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600 }}>Checked In</th>
            </tr></thead>
            <tbody>
              {rows.map((l: any) => (
                <tr key={l.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 16px', fontSize: 13 }}>{personName(l)}</td>
                  <td style={{ padding: '10px 16px' }}><span className="badge badge-gray" style={{ textTransform: 'capitalize' }}>{l.person_type}</span></td>
                  <td style={{ padding: '10px 16px', fontSize: 13 }}>{l.vehicle_reg || '—'}</td>
                  <td style={{ padding: '10px 16px', fontSize: 13 }}>{l.purpose || (l.host_name ? `Visiting ${l.host_name}` : '—')}</td>
                  <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-muted)' }}>{format(new Date(l.logged_at), 'd MMM, HH:mm')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function GateLog() {
  const [logs, setLogs] = useState<any[]>([]);
  const [counts, setCounts] = useState<any>({ total: 0, in_count: 0, out_count: 0, visitor_count: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [personType, setPersonType] = useState('');
  const [direction, setDirection] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { search, limit: 100 };
      if (personType) params.person_type = personType;
      if (direction) params.direction = direction;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = `${dateTo}T23:59:59`;
      const r = await api.get('/gate/logs', { params });
      setLogs(r.data.data.logs || []);
      setCounts(r.data.data);
    } finally { setLoading(false); }
  }, [search, personType, direction, dateFrom, dateTo]);

  useEffect(() => {
    const t = setTimeout(fetchLogs, 300);
    return () => clearTimeout(t);
  }, [fetchLogs]);

  const personName = (l: any) => {
    if (l.person_type === 'student') return `${l.student_first_name || ''} ${l.student_last_name || ''} (${l.student_number || 'Student'})`;
    if (l.person_type === 'staff') return `${l.staff_first_name || ''} ${l.staff_last_name || ''} (Staff)`;
    return `${l.visitor_name} (Visitor)`;
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 360 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 11, color: 'var(--text-muted)' }} />
          <input className="form-input" style={{ paddingLeft: 36 }} placeholder="Search by name or vehicle reg..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <button className={`btn btn-secondary ${showFilters ? 'btn-primary' : ''}`} onClick={() => setShowFilters(f => !f)}><Filter size={16} />Filters</button>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}><Plus size={16} />Log Entry/Exit</button>
      </div>

      {showFilters && (
        <div className="card" style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Person Type</label>
            <select className="form-input form-select" value={personType} onChange={e => setPersonType(e.target.value)}>
              <option value="">All</option>
              <option value="student">Student</option>
              <option value="staff">Staff</option>
              <option value="visitor">Visitor</option>
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Direction</label>
            <select className="form-input form-select" value={direction} onChange={e => setDirection(e.target.value)}>
              <option value="">All</option>
              <option value="in">In</option>
              <option value="out">Out</option>
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">From</label>
            <input type="date" className="form-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">To</label>
            <input type="date" className="form-input" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </div>
          {(personType || direction || dateFrom || dateTo) && (
            <button className="btn btn-secondary btn-sm" onClick={() => { setPersonType(''); setDirection(''); setDateFrom(''); setDateTo(''); }}>Clear</button>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div className="badge badge-gray" style={{ fontSize: 13, padding: '6px 14px' }}>{counts.total} total</div>
        <div className="badge badge-success" style={{ fontSize: 13, padding: '6px 14px' }}>{counts.in_count} in</div>
        <div className="badge badge-warning" style={{ fontSize: 13, padding: '6px 14px' }}>{counts.out_count} out</div>
        <div className="badge badge-info" style={{ fontSize: 13, padding: '6px 14px' }}>{counts.visitor_count} visitors</div>
      </div>

      {loading ? (
        <div className="card" style={{ textAlign: 'center', padding: 60 }}><div className="loader loader-dark" style={{ margin: '0 auto' }} /></div>
      ) : logs.length === 0 ? (
        <div className="card"><div className="empty-state"><DoorOpen size={48} /><p>No gate activity recorded yet.</p></div></div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: 'var(--bg)' }}>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600 }}>Person</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600 }}>Type</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600 }}>Direction</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600 }}>Vehicle</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600 }}>Purpose</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600 }}>Time</th>
            </tr></thead>
            <tbody>
              {logs.map((l: any) => (
                <tr key={l.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 16px', fontSize: 13 }}>{personName(l)}</td>
                  <td style={{ padding: '10px 16px' }}><span className="badge badge-gray" style={{ textTransform: 'capitalize' }}>{l.person_type}</span></td>
                  <td style={{ padding: '10px 16px' }}>
                    <span className={`badge badge-${l.direction === 'in' ? 'success' : 'warning'}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {l.direction === 'in' ? <LogIn size={12} /> : <LogOutIcon size={12} />}{l.direction === 'in' ? 'In' : 'Out'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 16px', fontSize: 13 }}>{l.vehicle_reg || '—'}</td>
                  <td style={{ padding: '10px 16px', fontSize: 13 }}>{l.purpose || (l.host_name ? `Visiting ${l.host_name}` : '—')}</td>
                  <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-muted)' }}>{format(new Date(l.logged_at), 'd MMM yyyy, HH:mm')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && <LogEntryModal onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); fetchLogs(); }} />}
    </div>
  );
}

function LogEntryModal({ onClose, onSaved }: any) {
  const [personType, setPersonType] = useState<'student' | 'staff' | 'visitor'>('visitor');
  const [direction, setDirection] = useState<'in' | 'out'>('in');
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>(null);
  const [form, setForm] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (personType === 'visitor' || search.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      const endpoint = personType === 'student' ? '/students' : '/staff';
      const r = await api.get(endpoint, { params: { search, limit: 8 } });
      setResults(personType === 'student' ? (r.data.data.students || []) : (r.data.data.staff || []));
    }, 300);
    return () => clearTimeout(t);
  }, [search, personType]);

  const submit = async () => {
    if (personType !== 'visitor' && !selected) { toast.error('Select a person first'); return; }
    if (personType === 'visitor' && !form.visitor_name) { toast.error('Visitor name is required'); return; }
    setLoading(true);
    try {
      const payload: any = { person_type: personType, direction, vehicle_reg: form.vehicle_reg, purpose: form.purpose };
      if (personType === 'student') payload.student_id = selected.id;
      else if (personType === 'staff') payload.staff_id = selected.id;
      else {
        payload.visitor_name = form.visitor_name;
        payload.visitor_id_number = form.visitor_id_number;
        payload.visitor_phone = form.visitor_phone;
        payload.host_name = form.host_name;
      }
      await api.post('/gate/logs', payload);
      toast.success('Gate entry logged');
      onSaved();
    } catch (err: any) { toast.error(err?.response?.data?.message || 'Failed to log entry'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Log Gate Entry/Exit</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)' }}>×</button></div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="grid-2" style={{ gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Person type</label>
              <select className="form-input form-select" value={personType} onChange={e => { setPersonType(e.target.value as any); setSelected(null); setResults([]); setSearch(''); }}>
                <option value="visitor">Visitor</option>
                <option value="student">Student</option>
                <option value="staff">Staff</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Direction</label>
              <select className="form-input form-select" value={direction} onChange={e => setDirection(e.target.value as any)}>
                <option value="in">In</option>
                <option value="out">Out</option>
              </select>
            </div>
          </div>

          {personType !== 'visitor' ? (
            <>
              <div className="form-group">
                <label className="form-label">Search {personType}</label>
                <input className="form-input" placeholder="Name or number..." value={search} onChange={e => { setSearch(e.target.value); setSelected(null); }} />
              </div>
              {results.length > 0 && !selected && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                  {results.map((r: any) => (
                    <div key={r.id} onClick={() => setSelected(r)} style={{ padding: '8px 12px', cursor: 'pointer', borderTop: '1px solid var(--border)' }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{r.first_name} {r.last_name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.student_number || r.staff_number || r.email}</div>
                    </div>
                  ))}
                </div>
              )}
              {selected && (
                <div className="badge badge-info" style={{ alignSelf: 'flex-start', padding: '6px 12px' }}>
                  Selected: {selected.first_name} {selected.last_name} ({selected.student_number || selected.staff_number})
                </div>
              )}
            </>
          ) : (
            <>
              <div className="form-group"><label className="form-label">Visitor Name *</label><input className="form-input" required value={form.visitor_name || ''} onChange={e => set('visitor_name', e.target.value)} /></div>
              <div className="grid-2" style={{ gap: 12 }}>
                <div className="form-group"><label className="form-label">ID Number</label><input className="form-input" value={form.visitor_id_number || ''} onChange={e => set('visitor_id_number', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={form.visitor_phone || ''} onChange={e => set('visitor_phone', e.target.value)} /></div>
              </div>
              <div className="form-group"><label className="form-label">Visiting (host name)</label><input className="form-input" value={form.host_name || ''} onChange={e => set('host_name', e.target.value)} /></div>
            </>
          )}

          <div className="grid-2" style={{ gap: 12 }}>
            <div className="form-group"><label className="form-label">Vehicle Reg</label><input className="form-input" value={form.vehicle_reg || ''} onChange={e => set('vehicle_reg', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Purpose</label><input className="form-input" value={form.purpose || ''} onChange={e => set('purpose', e.target.value)} /></div>
          </div>
        </div>
        <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={loading} onClick={submit}>{loading ? <span className="loader" /> : 'Log Entry'}</button></div>
      </div>
    </div>
  );
}

function GatePasses({ isGateStaff }: { isGateStaff: boolean }) {
  const { user } = useAuthStore();
  const canApprove = ['admin', 'registrar', 'superadmin'].includes(user?.role || '');
  const [passes, setPasses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [showModal, setShowModal] = useState(false);

  const fetchPasses = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/gate/passes', { params: filter ? { status: filter } : {} }); setPasses(r.data.data.passes || []); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchPasses(); }, [fetchPasses]);

  const requesterName = (p: any) => {
    if (p.student_first_name) return `${p.student_first_name} ${p.student_last_name} (${p.student_number})`;
    if (p.staff_first_name) return `${p.staff_first_name} ${p.staff_last_name} (Staff)`;
    return `${p.requested_by_first_name || ''} ${p.requested_by_last_name || ''}`;
  };

  const act = async (id: string, action: 'approve' | 'reject' | 'use') => {
    try {
      await api.patch(`/gate/passes/${id}/${action}`);
      toast.success(`Pass ${action === 'use' ? 'marked used' : action + 'd'}`);
      fetchPasses();
    } catch (err: any) { toast.error(err?.response?.data?.message || 'Action failed'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {[['', 'All'], ['pending', 'Pending'], ['approved', 'Approved'], ['used', 'Used'], ['rejected', 'Rejected']].map(([val, label]) => (
            <button key={val} className={`btn btn-sm ${filter === val ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter(val)}>{label}</button>
          ))}
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}><Plus size={16} />Request Pass</button>
      </div>

      {loading ? (
        <div className="card" style={{ textAlign: 'center', padding: 60 }}><div className="loader loader-dark" style={{ margin: '0 auto' }} /></div>
      ) : passes.length === 0 ? (
        <div className="card"><div className="empty-state"><DoorOpen size={48} /><p>No gate passes found.</p></div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {passes.map((p: any) => (
            <div key={p.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{requesterName(p)} <span className="badge badge-gray" style={{ marginLeft: 6, textTransform: 'capitalize' }}>{p.pass_type}</span></div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>{p.reason}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  Valid {format(new Date(p.valid_from), 'd MMM HH:mm')} – {format(new Date(p.valid_until), 'd MMM HH:mm')}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className={`badge badge-${p.status === 'approved' ? 'success' : p.status === 'used' ? 'info' : p.status === 'rejected' ? 'danger' : 'warning'}`} style={{ textTransform: 'capitalize' }}>{p.status}</span>
                {canApprove && p.status === 'pending' && (
                  <>
                    <button className="btn btn-secondary btn-sm" onClick={() => act(p.id, 'approve')}><Check size={13} />Approve</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => act(p.id, 'reject')}><XIcon size={13} />Reject</button>
                  </>
                )}
                {isGateStaff && p.status === 'approved' && (
                  <button className="btn btn-primary btn-sm" onClick={() => act(p.id, 'use')}>Mark Used</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && <RequestPassModal onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); fetchPasses(); }} />}
    </div>
  );
}

function RequestPassModal({ onClose, onSaved }: any) {
  const [form, setForm] = useState<any>({ pass_type: 'exit' });
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.reason || !form.valid_from || !form.valid_until) { toast.error('All fields are required'); return; }
    setLoading(true);
    try {
      await api.post('/gate/passes', form);
      toast.success('Gate pass requested');
      onSaved();
    } catch (err: any) { toast.error(err?.response?.data?.message || 'Failed to request pass'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Request Gate Pass</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)' }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Pass type</label>
              <select className="form-input form-select" value={form.pass_type} onChange={e => set('pass_type', e.target.value)}>
                <option value="exit">Exit</option>
                <option value="entry">Entry</option>
                <option value="general">General</option>
              </select>
            </div>
            <div className="form-group"><label className="form-label">Reason *</label><textarea className="form-input" required rows={3} value={form.reason || ''} onChange={e => set('reason', e.target.value)} /></div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group"><label className="form-label">Valid From *</label><input type="datetime-local" className="form-input" required value={form.valid_from || ''} onChange={e => set('valid_from', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Valid Until *</label><input type="datetime-local" className="form-input" required value={form.valid_until || ''} onChange={e => set('valid_until', e.target.value)} /></div>
            </div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : 'Submit Request'}</button></div>
        </form>
      </div>
    </div>
  );
}

const visitorStatusColor: Record<string, string> = {
  pending: 'warning', accepted: 'info', declined: 'danger', checked_in: 'success', checked_out: 'gray',
};

function VisitorRequests({ isGateStaff }: { isGateStaff: boolean }) {
  const { user } = useAuthStore();
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [issueModal, setIssueModal] = useState<any>(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/gate/visitor-requests', { params: filter ? { status: filter } : {} }); setRequests(r.data.data.visitor_requests || []); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const respond = async (id: string, decision: 'accept' | 'decline') => {
    try { await api.patch(`/gate/visitor-requests/${id}/${decision}`); toast.success(`Visitor ${decision}d`); fetchRequests(); }
    catch (err: any) { toast.error(err?.response?.data?.message || 'Action failed'); }
  };

  const release = async (id: string) => {
    try { await api.patch(`/gate/visitor-requests/${id}/release`); toast.success('Visitor released — clear to exit'); fetchRequests(); }
    catch (err: any) { toast.error(err?.response?.data?.message || 'Action failed'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[['', 'All'], ['pending', 'Pending'], ['accepted', 'Accepted'], ['checked_in', 'Checked In'], ['checked_out', 'Checked Out'], ['declined', 'Declined']].map(([val, label]) => (
            <button key={val} className={`btn btn-sm ${filter === val ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter(val)}>{label}</button>
          ))}
        </div>
        {isGateStaff && <button className="btn btn-primary" onClick={() => setShowNewModal(true)}><Plus size={16} />New Visitor Request</button>}
      </div>

      {loading ? (
        <div className="card" style={{ textAlign: 'center', padding: 60 }}><div className="loader loader-dark" style={{ margin: '0 auto' }} /></div>
      ) : requests.length === 0 ? (
        <div className="card"><div className="empty-state"><Users size={48} /><p>No visitor requests found.</p></div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {requests.map((vr: any) => {
            const isHost = vr.host_user_id === user?.id;
            return (
              <div key={vr.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{vr.visitor_name}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                    Visiting {vr.host_first_name} {vr.host_last_name}{vr.purpose ? ` — ${vr.purpose}` : ''}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{format(new Date(vr.created_at), 'd MMM yyyy, HH:mm')}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className={`badge badge-${visitorStatusColor[vr.status] || 'gray'}`} style={{ textTransform: 'capitalize' }}>{vr.status.replace('_', ' ')}</span>
                  {isHost && vr.status === 'pending' && (
                    <>
                      <button className="btn btn-secondary btn-sm" onClick={() => respond(vr.id, 'accept')}><Check size={13} />Accept</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => respond(vr.id, 'decline')}><XIcon size={13} />Decline</button>
                    </>
                  )}
                  {isGateStaff && vr.status === 'accepted' && (
                    <button className="btn btn-primary btn-sm" onClick={() => setIssueModal(vr)}>Issue Pass</button>
                  )}
                  {(isHost || isGateStaff) && vr.status === 'checked_in' && (
                    <button className="btn btn-secondary btn-sm" onClick={() => release(vr.id)}>Release Guest</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showNewModal && <NewVisitorRequestModal onClose={() => setShowNewModal(false)} onSaved={() => { setShowNewModal(false); fetchRequests(); }} />}
      {issueModal && <IssuePassModal vr={issueModal} onClose={() => setIssueModal(null)} onSaved={() => { setIssueModal(null); fetchRequests(); }} />}
    </div>
  );
}

function NewVisitorRequestModal({ onClose, onSaved }: any) {
  const [form, setForm] = useState<any>({});
  const [hostSearch, setHostSearch] = useState('');
  const [hostResults, setHostResults] = useState<any[]>([]);
  const [selectedHost, setSelectedHost] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (hostSearch.trim().length < 2) { setHostResults([]); return; }
    const t = setTimeout(async () => {
      const r = await api.get('/gate/hosts', { params: { search: hostSearch } });
      setHostResults(r.data.data.hosts || []);
    }, 300);
    return () => clearTimeout(t);
  }, [hostSearch]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.visitor_name) { toast.error('Visitor name is required'); return; }
    if (!selectedHost) { toast.error('Select who the visitor is here to see'); return; }
    setLoading(true);
    try {
      await api.post('/gate/visitor-requests', { ...form, host_user_id: selectedHost.id });
      toast.success('Visitor request sent to host');
      onSaved();
    } catch (err: any) { toast.error(err?.response?.data?.message || 'Failed to create visitor request'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>New Visitor Request</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)' }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group"><label className="form-label">Visitor Name *</label><input className="form-input" required value={form.visitor_name || ''} onChange={e => set('visitor_name', e.target.value)} /></div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group"><label className="form-label">ID Number</label><input className="form-input" value={form.visitor_id_number || ''} onChange={e => set('visitor_id_number', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={form.visitor_phone || ''} onChange={e => set('visitor_phone', e.target.value)} /></div>
            </div>
            <div className="form-group"><label className="form-label">Vehicle Reg</label><input className="form-input" value={form.vehicle_reg || ''} onChange={e => set('vehicle_reg', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Purpose</label><input className="form-input" value={form.purpose || ''} onChange={e => set('purpose', e.target.value)} /></div>
            <div className="form-group">
              <label className="form-label">Who are they visiting? *</label>
              {!selectedHost ? (
                <>
                  <input className="form-input" placeholder="Search staff by name..." value={hostSearch} onChange={e => setHostSearch(e.target.value)} />
                  {hostResults.length > 0 && (
                    <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginTop: 6 }}>
                      {hostResults.map((h: any) => (
                        <div key={h.id} onClick={() => setSelectedHost(h)} style={{ padding: '8px 12px', cursor: 'pointer', borderTop: '1px solid var(--border)' }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{h.first_name} {h.last_name}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'capitalize' }}>{h.role}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="badge badge-info" style={{ padding: '6px 12px', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <UserCircle size={14} />{selectedHost.first_name} {selectedHost.last_name}
                  <button type="button" onClick={() => { setSelectedHost(null); setHostSearch(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}><XIcon size={12} /></button>
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : 'Send Request'}</button></div>
        </form>
      </div>
    </div>
  );
}

function IssuePassModal({ vr, onClose, onSaved }: any) {
  const [loading, setLoading] = useState<'whatsapp' | 'print' | null>(null);

  const issue = async (delivery: 'whatsapp' | 'print') => {
    setLoading(delivery);
    try {
      await api.post(`/gate/visitor-requests/${vr.id}/issue-pass`, { pass_delivery: delivery });
      if (delivery === 'print') {
        const r = await api.get(`/gate/visitor-requests/${vr.id}/pass.pdf`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
        window.open(url, '_blank');
      }
      toast.success(delivery === 'whatsapp' ? 'Gate pass sent via WhatsApp' : 'Gate pass opened — use your browser to print');
      onSaved();
    } catch (err: any) { toast.error(err?.response?.data?.message || 'Failed to issue pass'); }
    finally { setLoading(null); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Issue Gate Pass — {vr.visitor_name}</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)' }}>×</button></div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>How should the visitor receive their gate pass?</p>
          <button className="btn btn-secondary" disabled={!!loading || !vr.visitor_phone} onClick={() => issue('whatsapp')}>
            {loading === 'whatsapp' ? <span className="loader" /> : <MessageCircle size={16} />}
            Send via WhatsApp{!vr.visitor_phone && ' (no phone on file)'}
          </button>
          <button className="btn btn-secondary" disabled={!!loading} onClick={() => issue('print')}>
            {loading === 'print' ? <span className="loader" /> : <Printer size={16} />}
            Print at gate
          </button>
        </div>
        <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button></div>
      </div>
    </div>
  );
}
